<template>
  <div class="h-100">
    <TreeSelect
      v-bind="$attrs"
      v-model="technicalPlaceId"
      v-model:modal-visible="selectModalVisibility"
      :entity-name="t('components.shared.searchSelect.TechnicalPlaceSelect.title')"
      :multiple="selectionMode === 'multiple'"
      :elements="technicalPlaces"
      :show-inline-create="showInlineCreate"
      :loading="loading || ancestorContextLoading"
      create-access-right="systemSettings.admin"
      :valid="valid"
      :disabled="disabled"
      :disabled-nodes="disabledTechnicalPlaces"
      :clearable="clearable"
      :auto-select-first="autoSelectFirst"
      :preferred-scroll-id="preferredScrollId"
      @show-create-modal="showCreateModal"
    />
    <OGTechnicalPlaceCreate
      v-if="createModalVisible"
      :show-in-modal="true"
      :site-id="
        props.variables.siteIds && props.variables.siteIds.length === 1
          ? (props.variables.siteIds[0] ?? undefined)
          : undefined
      "
      @created="setTechnicalPlace"
      @cancel="createModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, ref, type PropType } from "vue";
import { useTechnicalPlacesForSelectQuery, type OGTechnicalPlaceCreateMutation } from "@/graphql";
import TreeSelect from "@/components/shared/searchSelect/treeSelect/TreeSelect.vue";
import OGTechnicalPlaceCreate from "../../views/OGTechnicalPlaceCreate.vue";

const props = defineProps({
  variables: {
    type: Object as PropType<
      Partial<{
        ids: number[];
        siteIds: number[];
        hierarchyMinLevel: number;
        selfAndDescendantsOfTPId: number;
        selfAndDescendantsOfCCId: number;
        notSelfAndNotDescendantsOfCCId: number;
      }>
    >,
    required: false,
    default: () => ({}),
  },
  selectionMode: {
    type: String as PropType<"single" | "multiple">,
    required: false,
    default: "single",
  },
  autoSelectFirst: {
    type: Boolean,
    required: false,
    default: false,
  },
  preferredScrollId: {
    type: Number,
    required: false,
    default: undefined,
  },
  showInlineCreate: {
    type: Boolean,
    required: false,
    default: true,
  },
  valid: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabledNodes: {
    type: Array as PropType<
      {
        key: string | number;
        includeChildren: boolean;
      }[]
    >,
    required: false,
    default: undefined,
  },
});

const { result, loading } = useTechnicalPlacesForSelectQuery(computed(() => props.variables));

const hasDescendantRestriction = computed(() => props.variables.selfAndDescendantsOfTPId != null);

const { result: ancestorContextResult, loading: ancestorContextLoading } =
  useTechnicalPlacesForSelectQuery(
    computed(() => ({ siteIds: props.variables.siteIds })),
    computed(() => ({ enabled: hasDescendantRestriction.value })),
  );

const ancestorTechnicalPlaces = computed(() => {
  if (!hasDescendantRestriction.value) {
    return [];
  }

  const selectableTechnicalPlaces = result.value?.OGTechnicalPlaces ?? [];
  const selectablePaths = new Set(selectableTechnicalPlaces.map(({ path }) => path));
  const ancestorPaths = new Set(
    selectableTechnicalPlaces.flatMap(({ path }) => {
      const pathParts = path.split(".");

      return pathParts.slice(0, -1).map((_, index) => pathParts.slice(0, index + 1).join("."));
    }),
  );

  return (ancestorContextResult.value?.OGTechnicalPlaces ?? []).filter(
    ({ path }) => ancestorPaths.has(path) && !selectablePaths.has(path),
  );
});

const technicalPlaces = computed(() => [
  ...ancestorTechnicalPlaces.value,
  ...(result.value?.OGTechnicalPlaces ?? []),
]);

const disabledTechnicalPlaces = computed(() => [
  ...(props.disabledNodes ?? []),
  ...ancestorTechnicalPlaces.value.map(({ id }) => ({ key: id, includeChildren: false })),
]);

const technicalPlaceId = defineModel("modelValue", {
  type: [Array, Number] as PropType<number | number[] | undefined>,
  default: undefined,
});

const createModalVisible = ref(false);

function showCreateModal() {
  createModalVisible.value = true;
}

const selectModalVisibility = ref(false);

function setTechnicalPlace(cc: OGTechnicalPlaceCreateMutation["OGTechnicalPlaceCreate"]) {
  selectModalVisibility.value = false;
  createModalVisible.value = false;
  technicalPlaceId.value = cc.id;
}

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
